/* ============================================================
   wishlist.js — Wishlist with localStorage
   Persists product handles, updates button states and renders
   the wishlist page (search?view=wishlist) via AJAX fetch.
   ============================================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'joaninha:wishlist';
  const MAX_ITEMS = 50;
  const validHandle = h => typeof h === 'string' && /^[a-z0-9][a-z0-9-]*$/i.test(h);
  let memory = [];
  let storageWritable = true;
  let storageWarning = '';
  let storedMessages = {};
  try { storedMessages = JSON.parse(document.querySelector('[data-wishlist-strings]')?.textContent || '{}'); } catch (_) {}
  const defaults = {"loading": "Carregando seus favoritos…", "load_error": "Não foi possível carregar seus favoritos. Suas peças continuam salvas.", "partial_error": "Algumas peças não puderam ser carregadas. Seus favoritos continuam salvos.", "unavailable": "As peças salvas não estão disponíveis no momento.", "retry": "Tentar novamente", "temporary": "O navegador não permitiu salvar seus favoritos. Eles ficarão disponíveis somente nesta sessão.", "corrupt": "Não foi possível ler os favoritos salvos neste navegador.", "limit": "Você pode salvar até 50 peças. Remova uma para adicionar outra.", "share_error": "Não foi possível compartilhar automaticamente. Copie o link abaixo.", "copied": "Link copiado", "share_ready": "Link da sua lista", "removed": "Peça removida dos favoritos.", "cleaned": "Uma peça salva não está mais disponível e saiu da sua lista."};
  const message = key => storedMessages[key] || defaults[key];
  function announce(text) {
    let status = document.querySelector('[data-wishlist-feedback]');
    if (!status) { status = document.createElement('p'); status.className = 'od-toast'; status.dataset.wishlistFeedback = ''; status.setAttribute('role', 'status'); document.body.appendChild(status); }
    status.textContent = text;
    if (status.classList.contains('od-toast')) { status.classList.add('is-visible'); clearTimeout(status._hideTimer); status._hideTimer = setTimeout(() => status.classList.remove('is-visible'), 5000); }
  }
  const HEART_SVG = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/></svg>';
  const REMOVE_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>';

  function escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function getWishlist() {
    if (!storageWritable) return memory.slice();
    try {
      const val = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (val !== null && !Array.isArray(val)) throw new SyntaxError('Invalid saved list');
      memory = Array.isArray(val) ? [...new Set(val.filter(validHandle))].slice(0, MAX_ITEMS) : [];
    } catch (e) {
      storageWarning = message(e instanceof SyntaxError ? 'corrupt' : 'temporary');
      storageWritable = false;
      announce(storageWarning);
    }
    return memory.slice();
  }

  function saveWishlist(handles) {
    memory = [...new Set(handles.filter(validHandle))].slice(0, MAX_ITEMS);
    try { if (storageWritable) localStorage.setItem(STORAGE_KEY, JSON.stringify(memory)); }
    catch (_) { storageWritable = false; storageWarning = message('temporary'); announce(storageWarning); }
    updateButtons();
    updateCount();
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { handles: handles } }));
  }

  function toggleStore(handle) {
    handle = String(handle);
    if (!validHandle(handle)) return null;
    const handles = getWishlist();
    const idx = handles.indexOf(handle);
    if (idx >= 0) {
      handles.splice(idx, 1);
    } else {
      if (handles.length >= MAX_ITEMS) { announce(message('limit')); return null; }
      handles.push(handle);
    }
    saveWishlist(handles);
    return idx < 0; /* true if adicionado */
  }

  function toggle(handle) {
    const nowActive = toggleStore(handle);
    if (nowActive !== null && window.__wishlistToast) window.__wishlistToast(String(handle), nowActive);
    return nowActive;
  }

  function has(handle) {
    return getWishlist().indexOf(String(handle)) >= 0;
  }

  function remove(handle) {
    saveWishlist(getWishlist().filter(function (h) { return h !== String(handle); }));
  }

  function clear() {
    saveWishlist([]);
  }

  /* ---- UI ---- */
  function keyOf(btn) {
    return btn.dataset.productHandle || btn.dataset.variantId;
  }

  function updateButtons() {
    const handles = getWishlist();
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      const key = keyOf(btn);
      if (!key) return;
      const isActive = handles.indexOf(String(key)) >= 0;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      btn.setAttribute('data-active', isActive ? 'true' : 'false');
      btn.setAttribute('aria-label', isActive ? 'Remover da lista de desejos' : 'Adicionar à lista de desejos');
      const label = btn.querySelector('[data-wishlist-label]');
      if (label) label.textContent = isActive ? 'Salvo' : 'Salvar';
    });
  }

  /* ---- Wishlist toast (UX-05): nome do produto via data-product-title ---- */
  window.__wishlistToast = function (handle, added) {
    let title = null;
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      if (!title && btn.dataset.productHandle === String(handle)) {
        title = btn.dataset.productTitle || (btn.closest('[data-product-card]')?.querySelector('.card-product__title, [data-product-title]')?.textContent || '').trim();
      }
    });
    const msg = added
      ? (title ? '“' + title + '” adicionado à lista de desejos' : 'Adicionado à lista de desejos')
      : (title ? '“' + title + '” removido da lista de desejos' : 'Removido da lista de desejos');
    let t = document.querySelector('.od-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'od-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('is-visible');
    clearTimeout(t._hideTimer);
    t._hideTimer = setTimeout(function () { t.classList.remove('is-visible'); }, 2600);
  };

  function updateCount() {
    const count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  /* ---- Events ---- */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const key = keyOf(btn);
    if (!key) return;
    toggle(key);
    const page = btn.closest('[data-wishlist-page]');
    if (page) renderWishlistPage(page, true);
  });

  document.addEventListener('click', function (e) {
    const removeBtn = e.target.closest('[data-wishlist-remove]');
    if (!removeBtn) return;
    e.preventDefault();
    remove(removeBtn.dataset.handle || removeBtn.dataset.variantId);
    announce(message('removed'));
    const page = removeBtn.closest('[data-wishlist-page]');
    if (page) renderWishlistPage(page, true);
  });

  /* ---- Página da wishlist ---- */
  function emptyStateHTML() {
    const tpl = document.querySelector('template[data-wishlist-empty]');
    if (tpl && tpl.innerHTML.trim() !== '') return tpl.innerHTML;
    return '<div class="wishlist-empty"><p>Sua lista de desejos está vazia.</p><a href="/collections/all" class="btn">Explorar produtos</a></div>';
  }

  function formatPrice(cents) {
    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  function cardHTML(p) {
    const vid = (p.variants && p.variants[0]) ? p.variants[0].id : null;
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const img = p.featured_image
      ? '<img src="' + p.featured_image + (p.featured_image.indexOf('?') >= 0 ? '&' : '?') + 'width=900" alt="' + escapeHTML(p.title) + '" loading="lazy" class="card-product__img card-product__img--primary">'
      : '';
    const badges = [];
    if (onSale && p.available) badges.push('<span class="badge badge--sale">' + ((window.JoaninhaStrings && window.JoaninhaStrings.onSale) || 'Oferta') + '</span>');
    if (!p.available) badges.push('<span class="badge badge--solid">' + ((window.JoaninhaStrings && window.JoaninhaStrings.soldOut) || 'Esgotado') + '</span>');
    const price = '<span class="card-product__price">' +
      (onSale ? '<span class="price__compare">' + formatPrice(p.compare_at_price) + '</span>' : '') +
      '<span class="price__current">' + formatPrice(p.price) + '</span>' +
      '</span>';
    return '<div class="card-product" data-product-card>' +
      '<div class="card-product__media">' +
      '<a href="' + p.url + '" class="card-product__media-link" aria-label="' + escapeHTML(p.title) + '">' + img + '</a>' +
      (badges.length ? '<div class="card-product__badges">' + badges.join('') + '</div>' : '') +
      '<button class="card-product__wishlist" type="button" data-wishlist-toggle data-product-handle="' + escapeHTML(p.handle) + '" data-active="true" aria-pressed="true" aria-label="Remover da lista de desejos">' + HEART_SVG + '</button>' +
      '</div>' +
      '<div class="card-product__body">' +
      '<a href="' + p.url + '" class="card-product__title">' + escapeHTML(p.title) + '</a>' +
      price +
      (vid ? '<button class="btn card-product__quickadd" type="button" data-quick-add data-variant-id="' + vid + '">Adicionar à sacola</button>' : '') +
      '<div class="card-product__footer">' +
      '<a class="btn btn--outline" href="' + p.url + '">Ver produto</a>' +
      '<button class="wishlist-card__remove" type="button" data-wishlist-remove data-handle="' + escapeHTML(p.handle) + '">' + REMOVE_SVG + '<span>Remover</span></button>' +
      '</div>' +
      '</div>' +
      '</div>';
  }

  function recoveryHTML(text) {
    return '<div class="wishlist-error" role="status"><p>' + escapeHTML(text) + '</p><button type="button" class="btn btn--outline" data-wishlist-retry>' + escapeHTML(message('retry')) + '</button></div>';
  }
  async function fetchSavedProduct(handle) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch((window.Shopify?.routes?.root || '/') + 'products/' + encodeURIComponent(handle) + '.js', { signal: controller.signal });
      if (response.status === 404) return { unavailable: true };
      if (!response.ok) throw new Error('HTTP');
      const product = await response.json();
      if (!product || typeof product.title !== 'string' || !validHandle(product.handle) || !Number.isFinite(product.price) || typeof product.url !== 'string' || !product.url.startsWith('/') || (product.featured_image != null && typeof product.featured_image !== 'string')) throw new Error('Invalid product');
      return { product };
    } catch (_) { return { failed: true }; }
    finally { clearTimeout(timer); }
  }
  async function renderWishlistPage(container, restoreFocus = false) {
    if (!container) return;
    const version = (container._wishlistVersion || 0) + 1;
    container._wishlistVersion = version;
    const handles = getWishlist();
    updateShareButton();
    function focusResult() {
      if (!restoreFocus) return;
      container.querySelector('.card-product__title, .wishlist-empty a, [data-wishlist-retry]')?.focus({ preventScroll: true });
    }
    if (!handles.length) {
      updateToolbar([]);
      container.innerHTML = emptyStateHTML(); container.removeAttribute('aria-busy'); focusResult(); return;
    }
    container.setAttribute('aria-busy', 'true');
    container.innerHTML = '<div class="wishlist-loading" role="status">' + escapeHTML(message('loading')) + '</div>';
    const results = await Promise.all(handles.map(fetchSavedProduct));
    if (!container.isConnected || container._wishlistVersion !== version) return;
    const valid = results.filter(row => row.product).map(row => row.product);
    const gone = results.filter(row => row.unavailable).length;
    const failed = results.some(row => row.failed);
    if (gone && !failed) {
      saveWishlist(valid.map(function (p) { return p.handle; }));
      if (valid.length) announce(message('cleaned'));
    }
    if (!valid.length) {
      if (gone && !failed) {
        updateToolbar([]);
        container.innerHTML = emptyStateHTML();
        container.removeAttribute('aria-busy');
        focusResult();
        return;
      }
      container.innerHTML = recoveryHTML(message(failed ? 'load_error' : 'unavailable'));
    } else {
      const cols = window.theme?.gridColumns || 4;
      container.innerHTML = '<div class="grid grid--products" style="--grid-columns: ' + cols + ';">' + valid.map(cardHTML).join('') + '</div>';
    }
    updateToolbar(valid);
    container.removeAttribute('aria-busy'); updateButtons(); updateShareButton(); focusResult();
  }
  document.addEventListener('click', event => {
    const retry = event.target.closest('[data-wishlist-retry]');
    if (retry) renderWishlistPage(retry.closest('[data-wishlist-page]'), true);
  });

  /* ---- Toolbar: total + adicionar tudo ---- */
  function updateToolbar(products) {
    const bar = document.querySelector('[data-wishlist-toolbar]');
    if (!bar) return;
    const items = (products || []).filter(function (p) { return p && p.available && p.variants && p.variants[0]; });
    const countEl = bar.querySelector('[data-wl-count]');
    const totalEl = bar.querySelector('[data-wl-total]');
    if (countEl) countEl.textContent = items.length;
    if (totalEl) totalEl.textContent = formatPrice(items.reduce(function (sum, p) { return sum + p.price; }, 0));
    bar.hidden = items.length === 0;
    bar._wlItems = items.map(function (p) { return { id: p.variants[0].id, quantity: 1 }; });
  }

  async function addItemsToCart(items) {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    });
    if (!response.ok) throw new Error('cart');
    return response.json();
  }

  document.addEventListener('click', function (e) {
    const qa = e.target.closest('[data-quick-add]');
    if (qa) {
      e.preventDefault();
      const id = parseInt(qa.dataset.variantId, 10);
      if (!id) return;
      qa.disabled = true;
      addItemsToCart([{ id: id, quantity: 1 }]).then(function () {
        announce(message('added_cart'));
        qa.textContent = 'Na sacola ✓';
      }).catch(function () {
        announce(message('add_all_error'));
        qa.disabled = false;
      });
      return;
    }
    const all = e.target.closest('[data-wl-addall]');
    if (all) {
      e.preventDefault();
      const bar = all.closest('[data-wl-addall]') && document.querySelector('[data-wishlist-toolbar]');
      const items = bar && bar._wlItems ? bar._wlItems : [];
      if (!items.length) return;
      all.disabled = true;
      addItemsToCart(items).then(function () {
        announce(message('added_all'));
        window.location.href = (window.Shopify?.routes?.cart) || '/cart';
      }).catch(function () {
        announce(message('add_all_error'));
        all.disabled = false;
      });
    }
  });

  /* ---- Compartilhar ---- */
  function updateShareButton() {
    const btn = document.querySelector('[data-wishlist-share]');
    if (btn) btn.hidden = getWishlist().length === 0;
  }

  async function shareWishlist() {
    const handles = getWishlist();
    if (handles.length === 0) return;
    const url = window.location.origin + window.location.pathname + '?view=wishlist&items=' + handles.join(',');
    const data = { title: 'Meus favoritos na Joaninha', text: 'Dá uma olhada nos vestidos que separei', url: url };
    try {
      if (navigator.share && /mobile|android|iphone/i.test(navigator.userAgent)) {
        await navigator.share(data);
        return;
      }
      await navigator.clipboard.writeText(url);
      const btn = document.querySelector('[data-wishlist-share]');
      const label = btn ? btn.querySelector('[data-wishlist-share-label]') : null;
      if (label) {
        const original = label.textContent;
        label.textContent = message('copied');
        announce(message('copied'));
        setTimeout(function () { label.textContent = original; }, 2000);
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      announce(message('share_error'));
      const container = document.querySelector('[data-wishlist-share-recovery]');
      if (container) {
        container.hidden = false;
        const field = container.querySelector('input'); field.value = url; field.focus(); field.select();
      }
    }
  }

  /* ---- Hidratação via link compartilhado (?items=handle1,handle2) ---- */
  function hydrateFromURL() {
    const params = new URLSearchParams(window.location.search);
    const shared = params.get('items');
    if (!shared) return;
    const current = getWishlist();
    let added = 0;
    shared.split(',').forEach(function (raw) {
      const h = raw.trim();
      if (validHandle(h) && current.indexOf(h) < 0 && current.length < MAX_ITEMS) {
        current.push(h);
        added++;
      }
    });
    if (added > 0) saveWishlist(current);
    if (window.history && window.history.replaceState) {
      params.delete('items');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    }
  }

  /* ---- Init ---- */
  document.addEventListener('DOMContentLoaded', function () {
    updateButtons();
    updateCount();
    const pageContainer = document.querySelector('[data-wishlist-page]');
    if (pageContainer) {
      hydrateFromURL();
      const shareBtn = document.querySelector('[data-wishlist-share]');
      if (shareBtn) shareBtn.addEventListener('click', shareWishlist);
      renderWishlistPage(pageContainer);
    }
  });

  window.WishlistAPI = { getWishlist: getWishlist, toggle: toggle, has: has, remove: remove, clear: clear, updateButtons: updateButtons, updateCount: updateCount };
})();

